/**
 * TTML subtitles (1.9.0): reading any TTML (IMSC 1.0/1.1, EBU-TT-D, DFXP) and writing IMSC 1.1 Text or EBU-TT-D.
 *
 * Reading goes through the hardened in-house XML reader (sidecar.ts `parseXml`: no DOCTYPE, size, depth and element
 * limits) with `keepNodes`, so the order of text, `<span>` and `<br/>` is kept. Namespaces are resolved from the
 * document's own `xmlns` declarations (any prefix works); the TTML parameter and styling namespaces of TTML 1/2 and
 * of the 2006 DFXP drafts are understood.
 *
 *  - Timing: clock time (hh:mm:ss.fff, hh:mm:ss:ff with ttp:frameRate and ttp:frameRateMultiplier, dropNTSC), offset
 *    time (h, m, s, ms, f, t with ttp:tickRate), begin/end/dur with the parents' begins (body, div), timeBase media or
 *    smpte (programme timecode is handled after import, `programmeOffset`).
 *  - Styling: referential styles (chained), inline tts:* and region styles, inherited through region, body, div, p,
 *    span. Colour, background alpha and font size become the cue's style; italic, bold and underline become <i>, <b>,
 *    <u> in the text; <br/> a newline. The track style is the value most cues share; a cue keeps only what differs.
 *  - Placement: a region's origin, extent and displayAlign, and the textAlign, give the nearest of the 9 cells.
 *  - Language: the tt element's xml:lang.
 *  - What cannot be kept is reported in `warnings` (non-black backgrounds, custom regions, fonts, word colours).
 */
import { TICKS_PER_SECOND, type Rational } from './time.ts';
import { parseXml, XmlError, type XmlElement, type XmlLimits } from './sidecar.ts';
import { CUE_ALIGNS, DEFAULT_TRACK_STYLE, cueColumn, cueRow, cueRuns, effectiveCueStyle, stripLegacyAlign, type CueAlign, type CueStyle, type ResolvedCueStyle } from './caption.ts';
import type { Cue, TrackStyle } from './subtitles.ts';

/** The TTML profiles PRISM writes: IMSC 1.1 Text, and EBU-TT-D. */
export type TtmlProfile = 'imsc' | 'ebu-tt-d';

/** What a TTML read returns (the same shape as subtitle-formats.ts ParseResult, plus warnings). */
export interface TtmlParseResult {
  cues: Cue[];
  errors: Array<{ line: number; message: string }>;
  format: 'ttml';
  style?: TrackStyle;
  language?: string;
  warnings?: string[];
}

/** The XML limits TTML is read with: no DOCTYPE, 8 MB, 64 deep, 200 000 elements, the node order kept. */
export const TTML_XML_LIMITS: XmlLimits = { maxBytes: 8 * 1024 * 1024, maxDepth: 64, maxElements: 200_000, allowDoctype: false, keepNodes: true, what: 'TTML file' };

const TT_NS = new Set(['http://www.w3.org/ns/ttml', 'http://www.w3.org/2006/10/ttaf1', 'http://www.w3.org/2006/04/ttaf1']);

/**
 * Whether a text is a TTML document: a `<tt>` root (any prefix) in a TTML namespace, after an optional BOM, XML
 * declaration, comments and DOCTYPE (a DOCTYPE is then refused by `parseTtml`, so the file gets a clear error).
 */
export function isTtml(input: string): boolean {
  const head = input.slice(0, 8192).replace(/^﻿/, '');
  const m = /^\s*(?:<\?xml[^>]*\?>\s*)?(?:(?:<!--[\s\S]*?-->|<!DOCTYPE(?:[^>[]|\[[\s\S]*?\])*>|<\?[\s\S]*?\?>)\s*)*<(?:([\w.-]+):)?tt(?=[\s>/])([^>]*)>/.exec(head);
  if (!m) return false;
  const prefix = m[1];
  const attrs = m[2] ?? '';
  const re = prefix ? new RegExp(`xmlns:${prefix.replace(/[.]/g, '\\.')}\\s*=\\s*["']([^"']+)["']`) : /xmlns\s*=\s*["']([^"']+)["']/;
  const ns = re.exec(attrs)?.[1];
  return !!ns && TT_NS.has(ns.trim());
}

/* ---------------------------------------------------------------- namespaces and attributes */

type Kind = 'tt' | 'tts' | 'ttp' | 'ttm' | 'xml' | 'ebutts' | 'none' | 'other';

function nsKind(ns: string | undefined): Kind {
  if (!ns) return 'other';
  const n = ns.trim();
  if (TT_NS.has(n)) return 'tt';
  if (/^http:\/\/www\.w3\.org\/(ns\/ttml|2006\/(04|10)\/ttaf1)#styl(e|ing)$/.test(n)) return 'tts';
  if (/^http:\/\/www\.w3\.org\/(ns\/ttml|2006\/(04|10)\/ttaf1)#parameter$/.test(n)) return 'ttp';
  if (/^http:\/\/www\.w3\.org\/(ns\/ttml|2006\/(04|10)\/ttaf1)#metadata$/.test(n)) return 'ttm';
  if (n === 'urn:ebu:tt:style') return 'ebutts';
  return 'other';
}

/** An element with its attributes keyed `${kind}|${local}` and its element kind. */
interface Node {
  el: XmlElement;
  kind: Kind;
  local: string;
  attrs: Map<string, string>;
  parent: Node | null;
}

function annotate(root: XmlElement): Map<XmlElement, Node> {
  const out = new Map<XmlElement, Node>();
  const walk = (el: XmlElement, parent: Node | null, scope: Map<string, string>) => {
    const q = el.qattributes ?? {};
    let sc = scope;
    for (const [k, v] of Object.entries(q)) {
      if (k === 'xmlns' || k.startsWith('xmlns:')) {
        if (sc === scope) sc = new Map(scope);
        sc.set(k === 'xmlns' ? '' : k.slice(6), v);
      }
    }
    const attrs = new Map<string, string>();
    for (const [k, v] of Object.entries(q)) {
      if (k === 'xmlns' || k.startsWith('xmlns:')) continue;
      const i = k.indexOf(':');
      if (i < 0) { attrs.set(`none|${k}`, v); continue; }
      const prefix = k.slice(0, i), local = k.slice(i + 1);
      const kind: Kind = prefix === 'xml' ? 'xml' : nsKind(sc.get(prefix));
      attrs.set(`${kind}|${local}`, v);
    }
    const i = el.name.indexOf(':');
    const kind = nsKind(sc.get(i < 0 ? '' : el.name.slice(0, i)));
    const node: Node = { el, kind, local: el.local, attrs, parent };
    out.set(el, node);
    for (const c of el.children) walk(c, node, sc);
  };
  walk(root, null, new Map([['xml', 'http://www.w3.org/XML/1998/namespace']]));
  return out;
}

const childrenOf = (n: Node, nodes: Map<XmlElement, Node>, local: string): Node[] =>
  n.el.children.map((c) => nodes.get(c)!).filter((c) => c.kind === 'tt' && c.local === local);

/* ---------------------------------------------------------------- time */

interface TimeParams {
  frameRate: number;
  /** The effective rate as a fraction, for frame counts. */
  rateNum: number;
  rateDen: number;
  tickRate: number;
  subFrameRate: number;
  dropNtsc: boolean;
  timeBase: 'media' | 'smpte' | 'clock';
}

function timeParams(tt: Node): TimeParams {
  const fr = Number(tt.attrs.get('ttp|frameRate') ?? '') || 30;
  const mult = /^\s*(\d+)\s+(\d+)\s*$/.exec(tt.attrs.get('ttp|frameRateMultiplier') ?? '');
  const mn = mult ? Number(mult[1]) : 1, md = mult ? Number(mult[2]) || 1 : 1;
  const sub = Number(tt.attrs.get('ttp|subFrameRate') ?? '') || 1;
  const hasFrameRate = tt.attrs.has('ttp|frameRate');
  const tick = Number(tt.attrs.get('ttp|tickRate') ?? '') || (hasFrameRate ? (fr * mn) / md * sub : 1);
  const tb = tt.attrs.get('ttp|timeBase');
  return {
    frameRate: fr, rateNum: fr * mn, rateDen: md, tickRate: tick, subFrameRate: sub,
    dropNtsc: tt.attrs.get('ttp|dropMode') === 'dropNTSC',
    timeBase: tb === 'smpte' ? 'smpte' : tb === 'clock' ? 'clock' : 'media',
  };
}

/** A TTML time expression in seconds, or null when unreadable. */
export function parseTtmlTime(value: string, p: TimeParams): number | null {
  const v = value.trim();
  let m = /^(\d+):(\d{2}):(\d{2})(?:\.(\d+))?$/.exec(v);
  if (m) {
    const frac = m[4] ? Number(`0.${m[4]}`) : 0;
    return Number(m[1]) * 3600 + Number(m[2]) * 60 + Number(m[3]) + frac;
  }
  m = /^(\d+):(\d{2}):(\d{2})[:;](\d{2,})(?:\.(\d+))?$/.exec(v);
  if (m) {
    const h = Number(m[1]), mi = Number(m[2]), s = Number(m[3]), f = Number(m[4]);
    const sub = m[5] ? Number(m[5]) / p.subFrameRate : 0;
    let frames = ((h * 60 + mi) * 60 + s) * Math.round(p.frameRate) + f;
    if (p.dropNtsc) {
      const minutes = h * 60 + mi;
      frames -= 2 * (minutes - Math.floor(minutes / 10));
    }
    return ((frames + sub) * p.rateDen) / p.rateNum;
  }
  m = /^(\d+(?:\.\d+)?)(h|ms|m|s|f|t)$/.exec(v);
  if (m) {
    const n = Number(m[1]);
    switch (m[2]) {
      case 'h': return n * 3600;
      case 'm': return n * 60;
      case 's': return n;
      case 'ms': return n / 1000;
      case 'f': return (n * p.rateDen) / p.rateNum;
      case 't': return n / p.tickRate;
    }
  }
  return null;
}

/* ---------------------------------------------------------------- styles */

interface StyleSet {
  [key: string]: string;
}

const STYLE_KEYS = ['color', 'backgroundColor', 'fontSize', 'fontStyle', 'fontWeight', 'textDecoration', 'textAlign', 'displayAlign', 'fontFamily', 'origin', 'extent'] as const;

/** The inline tts:* attributes of a node. */
function inlineStyles(n: Node): StyleSet {
  const s: StyleSet = {};
  for (const k of STYLE_KEYS) {
    const v = n.attrs.get(`tts|${k}`);
    if (v !== undefined) s[k] = v.trim();
  }
  return s;
}

class StyleBook {
  private byId = new Map<string, Node>();
  private cache = new Map<string, StyleSet>();
  constructor(styles: Node[]) {
    for (const s of styles) {
      const id = s.attrs.get('xml|id');
      if (id) this.byId.set(id, s);
    }
  }
  /** The styles named by a `style` attribute (space-separated ids), in order, each with its own chain first. */
  resolve(refs: string | undefined, seen: Set<string> = new Set()): StyleSet {
    const out: StyleSet = {};
    for (const id of (refs ?? '').split(/\s+/).filter(Boolean)) {
      if (seen.has(id)) continue;
      const cached = this.cache.get(id);
      if (cached) { Object.assign(out, cached); continue; }
      const node = this.byId.get(id);
      if (!node) continue;
      const next = new Set(seen).add(id);
      const own: StyleSet = { ...this.resolve(node.attrs.get('none|style'), next), ...inlineStyles(node) };
      this.cache.set(id, own);
      Object.assign(out, own);
    }
    return out;
  }
  /** A content element's own specified styles: referential first, then inline. */
  specified(n: Node): StyleSet {
    return { ...this.resolve(n.attrs.get('none|style')), ...inlineStyles(n) };
  }
}

/** Named TTML colours (TTML 1 §8.3.2 named colours). */
const NAMED: Record<string, string> = {
  transparent: '#00000000', black: '#000000ff', silver: '#c0c0c0ff', gray: '#808080ff', white: '#ffffffff', maroon: '#800000ff',
  red: '#ff0000ff', purple: '#800080ff', fuchsia: '#ff00ffff', magenta: '#ff00ffff', green: '#008000ff', lime: '#00ff00ff',
  olive: '#808000ff', yellow: '#ffff00ff', navy: '#000080ff', blue: '#0000ffff', teal: '#008080ff', aqua: '#00ffffff', cyan: '#00ffffff',
};

/** A TTML colour as `{ hex: '#rrggbb', alpha: 0–1 }`, or null. */
export function parseTtmlColor(value: string | undefined): { hex: string; alpha: number } | null {
  if (!value) return null;
  const v = value.trim().toLowerCase();
  const named = NAMED[v];
  const src = named ?? v;
  let m = /^#([0-9a-f]{6})([0-9a-f]{2})?$/.exec(src);
  if (m) return { hex: `#${m[1]}`, alpha: m[2] ? parseInt(m[2], 16) / 255 : 1 };
  m = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/.exec(src);
  if (m) return { hex: `#${m[1]}${m[1]}${m[2]}${m[2]}${m[3]}${m[3]}`, alpha: 1 };
  m = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*(\d+)\s*)?\)$/.exec(src);
  if (m) {
    const h = (x: string) => Math.min(255, Number(x)).toString(16).padStart(2, '0');
    return { hex: `#${h(m[1]!)}${h(m[2]!)}${h(m[3]!)}`, alpha: m[4] !== undefined ? Math.min(255, Number(m[4])) / 255 : 1 };
  }
  return null;
}

/** A length's last component (the vertical one for two-value fontSize), as { n, unit }. */
function lengthOf(value: string | undefined, which: 'first' | 'last' = 'last'): { n: number; unit: string } | null {
  if (!value) return null;
  const parts = value.trim().split(/\s+/);
  const p = which === 'first' ? parts[0]! : parts[parts.length - 1]!;
  const m = /^([+-]?\d+(?:\.\d+)?)(px|em|c|%|rw|rh)$/.exec(p);
  return m ? { n: Number(m[1]), unit: m[2]! } : null;
}

/* ---------------------------------------------------------------- regions and cells */

interface Geometry {
  /** Fractions of the root container. */
  x: number; y: number; w: number; h: number;
}

/** The canonical cell regions PRISM writes (fractions). */
const ROW_GEOMETRY: Record<'t' | 'm' | 'b', { y: number; h: number; display: 'before' | 'center' | 'after' }> = {
  t: { y: 0.075, h: 0.3, display: 'before' },
  m: { y: 0.1, h: 0.8, display: 'center' },
  b: { y: 0.625, h: 0.3, display: 'after' },
};
const COL_TEXT_ALIGN: Record<'l' | 'c' | 'r', 'left' | 'center' | 'right'> = { l: 'left', c: 'center', r: 'right' };

function cellFrom(vertical: number, horizontal: number): CueAlign {
  const row = vertical < 1 / 3 ? 't' : vertical > 2 / 3 ? 'b' : 'm';
  const col = horizontal < 1 / 3 ? 'l' : horizontal > 2 / 3 ? 'r' : 'c';
  return `${row}${col}` as CueAlign;
}

/* ---------------------------------------------------------------- reading */

interface Collected {
  id: string | undefined;
  begin: number;
  end: number;
  text: string;
  speaker?: string;
  color?: { hex: string; alpha: number };
  background?: { hex: string; alpha: number };
  /** Font size as a fraction of the frame height. */
  fontFraction?: number;
  align?: CueAlign;
}

const fmtSize = (n: number) => Math.round(n * 100) / 100;

/** Read a TTML document (IMSC, EBU-TT-D, DFXP): cues with their styles and cells, the track style and language. */
export function parseTtml(input: string): TtmlParseResult {
  const fail = (message: string): TtmlParseResult => ({ cues: [], errors: [{ line: 1, message }], format: 'ttml' });
  let root: XmlElement;
  try {
    root = parseXml(input, TTML_XML_LIMITS);
  } catch (e) {
    return fail(e instanceof XmlError ? e.message : 'Not readable XML');
  }
  const nodes = annotate(root);
  const tt = nodes.get(root)!;
  if (tt.kind !== 'tt' || tt.local !== 'tt') return fail('The root element is not a TTML <tt>');
  const params = timeParams(tt);
  const warnings = new Set<string>();
  const errors: TtmlParseResult['errors'] = [];
  const head = childrenOf(tt, nodes, 'head')[0];
  const body = childrenOf(tt, nodes, 'body')[0];
  if (!body) return { cues: [], errors: [], format: 'ttml', ...(tt.attrs.get('xml|lang') ? { language: tt.attrs.get('xml|lang')! } : {}) };

  // Styles, regions and agents from head.
  const styling = head ? childrenOf(head, nodes, 'styling') : [];
  const styleNodes = styling.flatMap((s) => childrenOf(s, nodes, 'style'));
  const book = new StyleBook(styleNodes);
  const layout = head ? childrenOf(head, nodes, 'layout') : [];
  const regions = new Map<string, Node>();
  for (const l of layout) for (const r of childrenOf(l, nodes, 'region')) { const id = r.attrs.get('xml|id'); if (id) regions.set(id, r); }
  const agents = new Map<string, string>();
  for (const el of allNodes(head, nodes)) {
    if (el.kind === 'ttm' && el.local === 'agent') {
      const id = el.attrs.get('xml|id');
      const name = el.el.children.map((c) => nodes.get(c)!).find((c) => c.kind === 'ttm' && c.local === 'name');
      if (id && name?.el.text) agents.set(id, name.el.text);
    }
  }

  // Root extent and cell resolution for px and c lengths.
  const rootExtent = /^\s*(\d+(?:\.\d+)?)px\s+(\d+(?:\.\d+)?)px\s*$/.exec(tt.attrs.get('tts|extent') ?? '');
  const extentW = rootExtent ? Number(rootExtent[1]) : 1920, extentH = rootExtent ? Number(rootExtent[2]) : 1080;
  const cellRes = /^\s*(\d+)\s+(\d+)\s*$/.exec(tt.attrs.get('ttp|cellResolution') ?? '');
  const cellCols = cellRes ? Number(cellRes[1]) || 32 : 32, cellRows = cellRes ? Number(cellRes[2]) || 15 : 15;
  let pxAssumed = false;
  const toFraction = (l: { n: number; unit: string }, axis: 'w' | 'h'): number | null => {
    if (l.unit === '%') return l.n / 100;
    if (l.unit === 'px') { if (!rootExtent) pxAssumed = true; return l.n / (axis === 'w' ? extentW : extentH); }
    if (l.unit === 'c') return l.n / (axis === 'w' ? cellCols : cellRows);
    if (l.unit === 'rw') return axis === 'w' ? l.n / 100 : (l.n / 100) * (extentW / extentH);
    if (l.unit === 'rh') return axis === 'h' ? l.n / 100 : (l.n / 100) * (extentH / extentW);
    return null;
  };

  // A region's styles (its own `style` refs, inline tts:*, nested <style> children) and its cell.
  const regionStyle = new Map<string, StyleSet>();
  const regionCell = new Map<string, CueAlign | null>();
  let customRegions = 0;
  for (const [id, r] of regions) {
    const nested = r.el.children.map((c) => nodes.get(c)!).filter((c) => c.kind === 'tt' && c.local === 'style');
    const s: StyleSet = { ...book.resolve(r.attrs.get('none|style')) };
    for (const n of nested) Object.assign(s, inlineStyles(n));
    Object.assign(s, inlineStyles(r));
    regionStyle.set(id, s);
    const pair = (v: string | undefined) => {
      const parts = (v ?? '').trim().split(/\s+/);
      if (parts.length !== 2) return null;
      const a = lengthOf(parts[0], 'first'), b = lengthOf(parts[1], 'first');
      if (!a || !b) return null;
      const fx = toFraction(a, 'w'), fy = toFraction(b, 'h');
      return fx === null || fy === null ? null : [fx, fy] as const;
    };
    const origin = pair(s.origin) ?? [0, 0];
    const extent = pair(s.extent) ?? [1 - origin[0], 1 - origin[1]];
    const g: Geometry = { x: origin[0], y: origin[1], w: extent[0], h: extent[1] };
    const display = s.displayAlign === 'center' ? 'center' : s.displayAlign === 'after' ? 'after' : 'before';
    const v = display === 'before' ? g.y : display === 'after' ? g.y + g.h : g.y + g.h / 2;
    const ta = s.textAlign;
    const hpos = ta === 'left' || ta === 'start' ? g.x : ta === 'right' || ta === 'end' ? g.x + g.w : g.x + g.w / 2;
    const cell = cellFrom(v, hpos);
    regionCell.set(id, cell);
    const row = ROW_GEOMETRY[cueRow(cell)];
    const near = (a: number, b: number) => Math.abs(a - b) < 0.011;
    const canonical = near(g.x, 0.1) && near(g.w, 0.8) && near(g.y, row.y) && near(g.h, row.h) && display === row.display;
    if (!canonical) customRegions++;
  }

  // Walk the body: timing (par: children begin at the parent's begin) and inherited styles.
  const collected: Collected[] = [];
  let fontFamilies = false, wordColours = false, nonBlack = false;
  const time = (n: Node, k: string): number | null | undefined => {
    const v = n.attrs.get(`none|${k}`);
    if (v === undefined) return undefined;
    const t = parseTtmlTime(v, params);
    if (t === null) errors.push({ line: 1, message: `Unreadable time "${v}"` });
    return t;
  };
  type Inherited = { style: StyleSet; region?: string; begin: number; end: number | undefined; agent?: string };
  const visit = (n: Node, inh: Inherited) => {
    if (n.kind !== 'tt') return;
    const region = n.attrs.get('none|region') ?? inh.region;
    const b = time(n, 'begin');
    const e = time(n, 'end');
    const d = time(n, 'dur');
    if (b === null || e === null || d === null) return;
    const begin = inh.begin + (b ?? 0);
    let end = e !== undefined ? inh.begin + e : d !== undefined ? begin + d : inh.end;
    if (inh.end !== undefined && end !== undefined) end = Math.min(end, inh.end);
    const own = book.specified(n);
    // Inheritable styles (color, fontSize, fontStyle, fontWeight, textDecoration, textAlign, fontFamily) flow down;
    // backgroundColor is not inherited in TTML, but a p's or div's background is the cue's background here.
    const style: StyleSet = { ...inh.style, ...own };
    const agent = n.attrs.get('ttm|agent') ?? inh.agent;
    if (n.local === 'p') {
      if (begin === undefined || end === undefined || !Number.isFinite(end)) {
        errors.push({ line: 1, message: `A paragraph${n.attrs.get('xml|id') ? ` (${n.attrs.get('xml|id')})` : ''} has no end time` });
        return;
      }
      const rs = region ? regionStyle.get(region) : undefined;
      const full: StyleSet = { ...(rs ?? {}), ...style };
      collected.push(readParagraph(n, nodes, book, full, begin, end, region, agent));
      return;
    }
    for (const c of n.el.children) visit(nodes.get(c)!, { style, region, begin, end, agent });
  };
  const readParagraph = (p: Node, all: Map<XmlElement, Node>, sb: StyleBook, pStyle: StyleSet, begin: number, end: number, region: string | undefined, agent: string | undefined): Collected => {
    // Text runs with their flags, colour and background.
    type Run = { text: string; i: boolean; b: boolean; u: boolean; color?: string; bg?: string; br?: true };
    const runs: Run[] = [];
    let fontFraction: number | undefined;
    const fontOf = (s: StyleSet, parent: number): number => {
      const l = lengthOf(s.fontSize);
      if (!l) return parent;
      if (l.unit === '%') return (parent * l.n) / 100;
      if (l.unit === 'em') return parent * l.n;
      return toFraction(l, 'h') ?? parent;
    };
    // The font size: region, then body/div/p in that order, each % relative to the one before; the default is 1c.
    const chain: StyleSet[] = [];
    if (region && regionStyle.get(region)) chain.push(regionStyle.get(region)!);
    for (let a: Node | null = p; a && !(a.kind === 'tt' && a.local === 'tt'); a = a.parent) chain.splice(region && regionStyle.get(region) ? 1 : 0, 0, sb.specified(a));
    let base = 1 / cellRows;
    let sized = false;
    for (const s of chain) { if (s.fontSize) { sized = true; base = fontOf(s, base); } }
    if (sized) fontFraction = base;
    if (pStyle.fontFamily && !/^(default|sansSerif|proportionalSansSerif)$/i.test(pStyle.fontFamily)) fontFamilies = true;
    const flagsOf = (s: StyleSet) => ({ i: /^(italic|oblique)$/.test(s.fontStyle ?? ''), b: s.fontWeight === 'bold', u: /\bunderline\b/.test(s.textDecoration ?? '') && !/noUnderline/.test(s.textDecoration ?? '') });
    const walk = (n: Node, s: StyleSet, space: 'default' | 'preserve') => {
      for (const piece of n.el.nodes ?? []) {
        if ('text' in piece) {
          let t = piece.text;
          if (space !== 'preserve') t = t.replace(/[ \t\r\n]+/g, ' ');
          if (t) runs.push({ ...flagsOf(s), text: t, ...(s.color ? { color: s.color } : {}), ...(s.backgroundColor ? { bg: s.backgroundColor } : {}) });
          continue;
        }
        const c = all.get(piece.element)!;
        if (c.kind !== 'tt') continue;
        if (c.local === 'br') { runs.push({ ...flagsOf(s), text: '\n', br: true }); continue; }
        if (c.local !== 'span') continue;
        const cs = { ...s, ...sb.specified(c) };
        if (cs.fontFamily && !/^(default|sansSerif|proportionalSansSerif)$/i.test(cs.fontFamily)) fontFamilies = true;
        if (cs.fontSize && !sized) { fontFraction = fontOf(cs, base); sized = true; }
        walk(c, cs, piece.element.space ?? space);
      }
    };
    walk(p, pStyle, p.el.space ?? 'default');
    // Trim spaces around line breaks and at the ends (xml:space default), drop empty runs.
    const lines: Run[][] = [[]];
    for (const r of runs) { if (r.br) lines.push([]); else lines[lines.length - 1]!.push(r); }
    if (p.el.space !== 'preserve') {
      for (const line of lines) {
        if (line.length) { line[0]!.text = line[0]!.text.replace(/^ +/, ''); line[line.length - 1]!.text = line[line.length - 1]!.text.replace(/ +$/, ''); }
      }
    }
    const breaks = runs.filter((r) => r.br);
    const textRuns = lines.flatMap((l, i) => (i ? [breaks[i - 1]!, ...l] : l)).filter((r) => r.text);
    // The cue's colour and background: the value the visible runs share (else the most common, with a warning).
    const visible = textRuns.filter((r) => r.text.trim());
    const pick = (key: 'color' | 'bg', fallback: string | undefined): string | undefined => {
      const counts = new Map<string, number>();
      for (const r of visible) { const v = (r[key] ?? fallback ?? '').toLowerCase(); counts.set(v, (counts.get(v) ?? 0) + r.text.length); }
      if (counts.size > 1 && key === 'color') wordColours = true;
      let best: string | undefined, n = -1;
      for (const [v, c] of counts) if (c > n) { best = v; n = c; }
      return best || undefined;
    };
    const color = parseTtmlColor(pick('color', pStyle.color));
    const bg = parseTtmlColor(pick('bg', pStyle.backgroundColor));
    if (bg && bg.alpha > 0 && bg.hex !== '#000000') nonBlack = true;
    // The text with <i>, <b>, <u> where the flags change, properly nested: a tag turning off closes it and every tag
    // opened after it; tags still wanted are then reopened.
    let text = '';
    const stack: Array<'i' | 'b' | 'u'> = [];
    for (const r of textRuns) {
      {
        const want = { i: r.i, b: r.b, u: r.u };
        const cut = stack.findIndex((t) => !want[t]);
        if (cut >= 0) while (stack.length > cut) text += `</${stack.pop()}>`;
        for (const t of ['i', 'b', 'u'] as const) if (want[t] && !stack.includes(t)) { stack.push(t); text += `<${t}>`; }
      }
      text += escapeCueText(r.text);
    }
    while (stack.length) text += `</${stack.pop()}>`;
    text = text.replace(/<(i|b|u)>(\s*)<\/\1>/g, '$2');
    // The cell: the region's, else the paragraph's textAlign on the bottom row.
    let align = region ? regionCell.get(region) ?? undefined : undefined;
    const ta = pStyle.textAlign;
    if (ta) {
      const col = ta === 'left' || ta === 'start' ? 'l' : ta === 'right' || ta === 'end' ? 'r' : 'c';
      align = `${align ? cueRow(align) : 'b'}${col}` as CueAlign;
    }
    return {
      id: p.attrs.get('xml|id'), begin, end, text,
      ...(agent && agents.get(agent.split(/\s+/)[0]!) ? { speaker: agents.get(agent.split(/\s+/)[0]!) } : {}),
      ...(color ? { color } : {}), ...(bg ? { background: bg } : {}),
      ...(fontFraction !== undefined ? { fontFraction } : {}),
      ...(align ? { align } : {}),
    };
  };
  visit(body, { style: {}, region: undefined, begin: 0, end: undefined, agent: undefined });

  if (pxAssumed) warnings.add('Pixel sizes were read against 1920×1080 (the file states no tts:extent)');
  if (customRegions) warnings.add(`${customRegions === 1 ? 'A custom region was' : `${customRegions} custom regions were`} mapped to the nearest of the 9 cells`);
  if (fontFamilies) warnings.add('Fonts were not kept (PRISM draws subtitles in its own font)');
  if (wordColours) warnings.add('Colours of single words were not kept (a cue has one colour)');
  if (nonBlack) warnings.add('A non-black background was drawn black (only its opacity is kept)');

  // Resolve each cue's values and factor out the track style: per field the value most cues share.
  const resolved = collected.map((c) => ({
    ...(c.align ? { align: c.align } : {}),
    ...(c.color ? { color: c.color.hex } : {}),
    ...(c.background ? { background: Math.round(c.background.alpha * 100) / 100 } : {}),
    ...(c.fontFraction !== undefined ? { size: Math.min(2, Math.max(0.5, fmtSize(c.fontFraction / 0.045))) } : {}),
  }) as CueStyle);
  const track: TrackStyle = {};
  for (const k of ['align', 'color', 'background', 'size'] as const) {
    const counts = new Map<string, { v: string | number; n: number }>();
    for (const r of resolved) {
      const v = (r[k] ?? DEFAULT_TRACK_STYLE[k]) as string | number;
      const e = counts.get(String(v)) ?? { v, n: 0 };
      e.n++; counts.set(String(v), e);
    }
    let best: { v: string | number; n: number } | undefined;
    for (const e of counts.values()) if (!best || e.n > best.n || (e.n === best.n && e.v === DEFAULT_TRACK_STYLE[k])) best = e;
    if (best && best.v !== DEFAULT_TRACK_STYLE[k]) (track as Record<string, unknown>)[k] = best.v;
  }
  const trackResolved = effectiveCueStyle(track, { text: '' });
  const used = new Set<string>();
  const cues: Cue[] = [];
  const indexed = collected.map((c, i) => ({ c, r: resolved[i]! }));
  indexed.sort((a, b) => a.c.begin - b.c.begin);
  indexed.forEach(({ c, r }) => {
    const style: CueStyle = {};
    for (const k of ['align', 'color', 'background', 'size'] as const) {
      const v = (r[k] ?? DEFAULT_TRACK_STYLE[k]) as never;
      if (v !== trackResolved[k]) (style as Record<string, unknown>)[k] = v;
    }
    let id = c.id && /^[\w.:-]{1,64}$/.test(c.id) && !used.has(c.id) ? c.id : `c${cues.length + 1}`;
    while (used.has(id)) id = `${id}_`;
    used.add(id);
    const startTicks = Math.max(0, Math.round(c.begin * TICKS_PER_SECOND));
    const endTicks = Math.max(0, Math.round(c.end * TICKS_PER_SECOND));
    if (endTicks <= startTicks) { errors.push({ line: 1, message: `A paragraph (${c.id ?? id}) ends before it starts` }); return; }
    cues.push({ id, startTicks, endTicks, text: c.text.slice(0, 2000), ...(c.speaker ? { speaker: c.speaker.slice(0, 120) } : {}), ...(Object.keys(style).length ? { style } : {}) });
  });
  const language = tt.attrs.get('xml|lang')?.trim();
  return {
    cues, errors, format: 'ttml',
    ...(Object.keys(track).length ? { style: track } : {}),
    ...(language ? { language } : {}),
    ...(warnings.size ? { warnings: [...warnings] } : {}),
  };
}

function allNodes(n: Node | undefined, nodes: Map<XmlElement, Node>): Node[] {
  if (!n) return [];
  const out: Node[] = [];
  const walk = (x: Node) => { out.push(x); x.el.children.forEach((c) => walk(nodes.get(c)!)); };
  walk(n);
  return out;
}

/** Text read from XML into cue text: a `<` or `&` that cue markup would read is written as an entity. */
const escapeCueText = (s: string) => s.replace(/&(?=#?\w+;)/g, '&amp;').replace(/<(?=[a-zA-Z/])/g, '&lt;');

/* ---------------------------------------------------------------- writing */

const xmlEscape = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const attrEscape = (s: string) => xmlEscape(s).replace(/"/g, '&quot;');
const pct = (n: number) => `${Math.round(n * 10000) / 100}%`;

function mediaTime(ticks: number): string {
  const totalMs = Math.round((ticks * 1000) / TICKS_PER_SECOND);
  const p = (n: number, w = 2) => String(n).padStart(w, '0');
  return `${p(Math.floor(totalMs / 3600000))}:${p(Math.floor(totalMs / 60000) % 60)}:${p(Math.floor(totalMs / 1000) % 60)}.${p(totalMs % 1000, 3)}`;
}

/** The cell resolution PRISM writes; a size of 1 is 4.5 % of the frame height, i.e. 135 % of a 1/30 cell. */
const CELL_ROWS = 30;
const sizePercent = (size: number) => Math.round(size * 0.045 * CELL_ROWS * 10000) / 100;
const alphaHex = (a: number) => Math.round(Math.min(1, Math.max(0, a)) * 255).toString(16).padStart(2, '0');

/**
 * Write a track as TTML in the given profile, with media time hh:mm:ss.mmm: one region per cell used, one style per
 * style combination (font size and text alignment on the paragraph, colour, background and italic/bold/underline on
 * spans, since a TTML background is not inherited and paints the span's line area). IMSC 1.1 Text carries its content
 * profile; EBU-TT-D its ebuttm document metadata, ttp:cellResolution, ebutts:linePadding and font sizes in %.
 * Speakers are ttm:agent in IMSC and a "Name: " prefix in EBU-TT-D (which has no agents).
 */
export function serializeTtml(track: { language: string; label?: string; style?: TrackStyle }, cues: Cue[], opts: { profile: TtmlProfile; frameRate: Rational }): string {
  const ebu = opts.profile === 'ebu-tt-d';
  const sorted = [...cues].sort((a, b) => a.startTicks - b.startTicks);
  const styles = new Map<string, string>(); // attribute string → id
  const styleId = (attrs: string): string => {
    let id = styles.get(attrs);
    if (!id) { id = `s${styles.size + 1}`; styles.set(attrs, id); }
    return id;
  };
  const cells = new Set<CueAlign>();
  const agents = new Map<string, string>();
  const paragraphs: string[] = [];
  const usedIds = new Set<string>();
  for (const cue of sorted) {
    const s: ResolvedCueStyle = effectiveCueStyle(track.style, cue);
    cells.add(s.align);
    const pAttrs = `tts:textAlign="${COL_TEXT_ALIGN[cueColumn(s.align)]}" tts:fontSize="${sizePercent(s.size)}%"${ebu ? ' ebutts:linePadding="0.5c"' : ''}`;
    const pStyle = styleId(pAttrs);
    let text = stripLegacyAlign(cue.text);
    if (cue.speaker && ebu) text = `${cue.speaker}: ${text}`;
    const runs = cueRuns(text);
    const spanBase = `tts:color="${s.color}" tts:backgroundColor="#000000${alphaHex(s.background)}"`;
    const spans = runs.map((r) => {
      const flags = `${r.i ? ' tts:fontStyle="italic"' : ''}${r.b ? ' tts:fontWeight="bold"' : ''}${r.u ? ' tts:textDecoration="underline"' : ''}`;
      const id = styleId(`${spanBase}${flags}`);
      const inner = r.text.split('\n').map(xmlEscape).join('<br/>');
      return `<span style="${id}">${inner}</span>`;
    }).join('');
    let id = /^[A-Za-z_][\w.-]*$/.test(cue.id) ? cue.id : `c_${cue.id.replace(/[^\w.-]/g, '_')}`;
    while (usedIds.has(id)) id = `${id}_`;
    usedIds.add(id);
    let agentAttr = '';
    if (cue.speaker && !ebu) {
      let a = agents.get(cue.speaker);
      if (!a) { a = `agent${agents.size + 1}`; agents.set(cue.speaker, a); }
      agentAttr = ` ttm:agent="${a}"`;
    }
    paragraphs.push(`      <p xml:id="${attrEscape(id)}" begin="${mediaTime(cue.startTicks)}" end="${mediaTime(cue.endTicks)}" region="r_${s.align}" style="${pStyle}"${agentAttr}>${spans}</p>`);
  }
  const ns = [
    'xmlns="http://www.w3.org/ns/ttml"',
    'xmlns:ttp="http://www.w3.org/ns/ttml#parameter"',
    'xmlns:tts="http://www.w3.org/ns/ttml#styling"',
    'xmlns:ttm="http://www.w3.org/ns/ttml#metadata"',
    ...(ebu ? ['xmlns:ebuttm="urn:ebu:tt:metadata"', 'xmlns:ebutts="urn:ebu:tt:style"'] : []),
  ];
  const lang = attrEscape(track.language || 'und');
  const rate = opts.frameRate;
  const fr = Math.round(rate.num / rate.den);
  const gcd = (a: number, b: number): number => (b ? gcd(b, a % b) : a);
  const g = gcd(rate.num, fr * rate.den) || 1;
  const mult = rate.num === fr * rate.den ? '' : ` ttp:frameRateMultiplier="${rate.num / g} ${(fr * rate.den) / g}"`;
  const ttAttrs = [
    ...ns,
    ...(ebu ? [] : ['ttp:contentProfiles="http://www.w3.org/ns/ttml/profile/imsc1.1/text"']),
    'ttp:timeBase="media"',
    `ttp:frameRate="${fr}"${mult}`,
    `ttp:cellResolution="50 ${CELL_ROWS}"`,
    `xml:lang="${lang}"`,
  ];
  const metadata: string[] = [];
  if (ebu) {
    metadata.push('    <metadata>', '      <ebuttm:documentMetadata>', '        <ebuttm:conformsToStandard>urn:ebu:tt:distribution:2018-04</ebuttm:conformsToStandard>');
    if (track.label) metadata.push(`        <ebuttm:documentTitle>${xmlEscape(track.label)}</ebuttm:documentTitle>`);
    metadata.push('      </ebuttm:documentMetadata>', '    </metadata>');
  } else if (track.label || agents.size) {
    metadata.push('    <metadata>');
    if (track.label) metadata.push(`      <ttm:title>${xmlEscape(track.label)}</ttm:title>`);
    for (const [name, id] of agents) metadata.push(`      <ttm:agent xml:id="${id}" type="person"><ttm:name type="full">${xmlEscape(name)}</ttm:name></ttm:agent>`);
    metadata.push('    </metadata>');
  }
  const regionLines = CUE_ALIGNS.filter((c) => cells.has(c)).map((c) => {
    const g = ROW_GEOMETRY[cueRow(c)];
    return `      <region xml:id="r_${c}" tts:origin="10% ${pct(g.y)}" tts:extent="80% ${pct(g.h)}" tts:displayAlign="${g.display}"/>`;
  });
  const styleLines = [...styles].map(([attrs, id]) => `      <style xml:id="${id}" ${attrs}/>`);
  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    `<tt ${ttAttrs.join(' ')}>`,
    '  <head>',
    ...metadata,
    '    <styling>',
    ...styleLines,
    '    </styling>',
    '    <layout>',
    ...regionLines,
    '    </layout>',
    '  </head>',
    '  <body>',
    '    <div>',
    ...paragraphs,
    '    </div>',
    '  </body>',
    '</tt>',
    '',
  ].join('\n');
}
